import type { MouseEvent, ReactNode } from "react";
import cx from "ui/cx";
import IconEllipsis from "ui/icons/IconEllipsis";
import type { PopoverContentProps, PopoverOpenChangeDetails } from "ui/popovers/Popover";
import { Popover } from "ui/popovers/Popover";
import styles from "./OverflowMenu.module.scss";

export type OverflowMenuProps = {
  children?: ReactNode;
  menuItems?: { children: ReactNode; onClick?: (event: MouseEvent<HTMLLIElement>) => void }[];
  className?: string;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
  alignOffset?: number;
  disabled?: boolean;
  open?: boolean;
  /** Forwards Base UI's change details (e.g. `details.reason`) for consumers that need them. */
  onOpenChange?: (open: boolean, details?: PopoverOpenChangeDetails) => void;
  title?: ReactNode;
  showCloseIcon?: boolean;
} & Pick<PopoverContentProps, "collisionBoundary">;

export default function OverflowMenu({
  children,
  menuItems,
  className,
  side = "bottom",
  align = "center",
  sideOffset = 4,
  alignOffset,
  disabled = false,
  title,
  showCloseIcon = false,
  open,
  onOpenChange,
  collisionBoundary,
}: OverflowMenuProps) {
  const hasHeader = !!title || showCloseIcon;
  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger className={cx(styles.trigger, disabled && styles.disabled, className)}>
        <button
          type="button"
          disabled={disabled}
          aria-label="More options"
          className={styles.triggerButton}
          data-testid="overflow-menu-trigger"
        >
          <IconEllipsis />
        </button>
      </Popover.Trigger>
      <Popover.Content
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        className={cx(styles.content, hasHeader && styles.hasHeader)}
        data-testid="overflow-menu-content"
        collisionBoundary={collisionBoundary}
      >
        {hasHeader && (
          <div className={styles.header} data-testid="overflow-menu-header">
            {title}
            <Popover.Close data-testid="overflow-menu-close" />
          </div>
        )}
        {menuItems && (
          <ul className={styles.menuItems} data-testid="overflow-menu-items">
            {menuItems.map((item, i) => (
              <li key={i} onClick={item.onClick} data-testid={`overflow-menu-item-${i}`}>
                {item.children}
              </li>
            ))}
          </ul>
        )}
        {children && (
          <div className={styles.children} data-testid="overflow-menu-children">
            {children}
          </div>
        )}
      </Popover.Content>
    </Popover.Root>
  );
}
